import React, { Component } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { markVisitSent, rememberSource, recordAction, recordUnresolvedSlug, getVisitorId } from '../tracking';

// Catches `/<slug>` URLs — ONE path serving TWO namespaces.
//
//   · a link CODE      (animates.app/zh8b)      → link_sources, recorded as a click
//   · a DISPLAY NAME   (animates.app/knight899) → an affiliate, recorded as before
//
// GET /l/resolve/:slug answers for both in a single call and says which it got.
// Codes win; that ordering is what makes a 4-character code safe inside a
// namespace real users can register (a-server/utils/linkCodes.js).
//
// Mirrors ALLOWED_REDIRECT_HOSTS in a-server/routes/links.js. See goTo() below.
const ALLOWED_DESTINATION_HOSTS = ['animates.app', 'www.animates.app', 'apps.apple.com', 'play.google.com'];

// 🔴 THREE THINGS IN HERE ARE LOAD-BEARING FOR REAL REVENUE and none of them
//    changed: the `affiliate_ref` localStorage key, its `{ref, capturedAt}` shape,
//    and therefore the `ref` field SignUp.js / SignUpInfo.js / CommunitySignUp.js
//    later attach to the signup POST. Four founding affiliates are live
//    revenue-share relationships hanging off exactly those three things.
class AffiliateLanding extends Component {
    constructor(props) {
        super(props);
        this.state = { status: 'checking', to: '/' };
    }

    componentDidMount() {
        const slug = this.getSlug();
        if (!slug) {
            this.setState({ status: 'not_found' });
            return;
        }
        // 🔒 THE TIMEOUT IS PART OF THE FALLBACK, not a nicety. axios has no default
        //    one, so a server that accepts the connection and never answers would
        //    leave an affiliate's visitor on a spinner forever and the fallback
        //    below would never run. Only the NEW call carries it; the fallback is
        //    left with today's exact semantics.
        axios.get(this.resolveUrl(slug), { timeout: 8000 })
            .then((res) => {
                const data = res && res.data;
                if (!data || data.success !== true || !data.kind) {
                    // An unexpected body is treated as a failure, not as `none` —
                    // it drops into the affiliate fallback below rather than
                    // silently dropping an affiliate's click.
                    throw new Error('unexpected_resolve_body');
                }
                if (data.kind === 'source') {
                    // The SERVER already inserted this click (entry='path'). Marking
                    // the session as counted is what stops index.js's beacon from
                    // adding a second row for the same visit on the next load.
                    markVisitSent(data.click_id, data.code);
                    rememberSource({ code: data.code, platform: data.platform, placement: data.placement });
                    recordAction('landing_view'); // the ghost filter — see 01-DESIGN.md Tier 1
                    this.goTo(data.destination_url);
                    return;
                }
                if (data.kind === 'affiliate') {
                    this.storeAffiliateRef(slug);
                    this.setState({ status: 'redirect', to: this.withQuery('/') });
                    return;
                }
                // kind === 'none'. Behaves exactly as today for the visitor, but the
                // dead slug is logged so a wrong link printed somewhere in the world
                // shows up on the dashboard instead of vanishing.
                recordUnresolvedSlug(slug);
                this.setState({ status: 'not_found', to: this.withQuery('/') });
            })
            .catch(() => {
                this.resolveViaAffiliatesFallback(slug);
            });
    }

    // 🔒 THE FALLBACK. GET /affiliates/resolve/:displayName is unchanged and still
    //    mounted, so if /l/resolve is missing, half-deployed or failing, an
    //    affiliate's link still works exactly as it did before this round. Ten
    //    lines, and they mean a server mistake can never cost the four founding
    //    affiliates a referral.
    resolveViaAffiliatesFallback(slug) {
        axios.get(process.env.REACT_APP_SERVER_API + '/affiliates/resolve/' + encodeURIComponent(slug))
            .then((res) => {
                const exists = res && res.data && res.data.data && res.data.data.exists;
                if (!exists) {
                    this.setState({ status: 'not_found', to: this.withQuery('/') });
                    return;
                }
                this.storeAffiliateRef(slug);
                this.setState({ status: 'redirect', to: this.withQuery('/') });
            })
            .catch(() => {
                this.setState({ status: 'not_found', to: this.withQuery('/') });
            });
    }

    // Unchanged key, unchanged shape. SignUp.js reads `parsed.ref` off this.
    storeAffiliateRef(slug) {
        try {
            localStorage.setItem('affiliate_ref', JSON.stringify({
                ref: slug,
                capturedAt: Date.now(),
            }));
        } catch (_) { /* storage disabled — proceed without persistence */ }
    }

    // The landing page's own context, passed explicitly. Without it the click row
    // records this API call's query string as if it were the visitor's URL, and
    // the visit is never joined to the visitor.
    resolveUrl(slug) {
        const params = new URLSearchParams();
        params.set('path', window.location.pathname || ('/' + slug));
        if (window.location.search) params.set('q', window.location.search);
        // ⚠️ THE SENTINEL IS NOT PADDING. a-server/routes/links.js falls back to
        //    `req.headers.referer` when this param is absent — and on this
        //    cross-origin XHR that header is OUR OWN origin, so every bio-link tap
        //    with no real referrer (the common case) would be filed under
        //    www.animates.app instead of "no referrer". A non-URL value parses to
        //    referrer_host NULL, which is the honest answer. Reported to the server
        //    side as well; this makes the data right without waiting for that.
        params.set('referrer', document.referrer || '(none)');
        const visitorId = getVisitorId();
        if (visitorId) params.set('visitor_id', visitorId);
        return process.env.REACT_APP_SERVER_API + '/l/resolve/' + encodeURIComponent(slug) + '?' + params.toString();
    }

    // ⚠️ PRE-EXISTING BUG FIXED IN PASSING, flagged for veto: `<Navigate to="/" />`
    //    threw the query string away, so any ?utm_* on an affiliate link was already
    //    being lost before it could be recorded. It is carried through now.
    withQuery(path) {
        const search = window.location.search || '';
        if (!search) return path;
        return path + (path.indexOf('?') === -1 ? search : '&' + search.replace(/^\?/, ''));
    }

    // destination_url is owner-controlled and the server's allowlist permits other
    // hosts (apps.apple.com, play.google.com), so it may be ABSOLUTE. <Navigate>
    // only understands in-app paths — an absolute one is a real navigation.
    //
    // 🔒 THE HOST CHECK MIRRORS a-server/routes/links.js ALLOWED_REDIRECT_HOSTS,
    //    and it is here because the server applies that list on GET /r/:code but
    //    NOT to the value it hands back from /l/resolve. Reported to the server
    //    side; until it lands, this is the only thing standing between a bad row
    //    in link_sources and an open redirect wearing the animates.app name.
    //    Anything unrecognised lands on '/' — the same place a dead link lands.
    goTo(destination) {
        const target = typeof destination === 'string' ? destination.trim() : '';
        if (!target) {
            this.setState({ status: 'redirect', to: this.withQuery('/') });
            return;
        }
        if (/^https?:\/\//i.test(target)) {
            if (ALLOWED_DESTINATION_HOSTS.indexOf(this.hostOf(target)) !== -1) {
                window.location.replace(target);
            } else {
                this.setState({ status: 'redirect', to: this.withQuery('/') });
            }
            return;
        }
        // A relative destination must stay one. '//evil.example' is protocol-relative
        // and would leave the site entirely, and a backslash is normalised to '/' by
        // the URL parser, so '/\\evil.example' is the same attack wearing a hat —
        // and it makes history.pushState throw rather than navigate.
        if (target.charAt(0) !== '/' || target.charAt(1) === '/' || target.indexOf('\\') !== -1) {
            this.setState({ status: 'redirect', to: this.withQuery('/') });
            return;
        }
        this.setState({ status: 'redirect', to: this.withQuery(target) });
    }

    hostOf(url) {
        try {
            return new URL(url).hostname.toLowerCase();
        } catch (_) {
            return '';
        }
    }

    getSlug() {
        // Pull the first path segment so /:displayName routing isn't required to match exactly.
        const path = (window.location.pathname || '').replace(/^\/+/, '').replace(/\/+$/, '');
        if (!path) return '';
        return path.split('/')[0];
    }

    render() {
        if (this.state.status === 'redirect') {
            // Click is counted and `affiliate_ref` is stored before this redirect fires, so attribution
            // works regardless of destination. Sending to "/" lets the visitor see the splash and self-select
            // free vs. paid (or post-launch, sign-up vs. sign-in) — works the same pre- and post-launch.
            return <Navigate to={this.state.to} replace />;
        }
        if (this.state.status === 'not_found') {
            return <Navigate to={this.state.to} replace />;
        }
        return (
            <div className="loading-screen">
                <div className="loading-spinner"></div>
            </div>
        );
    }
}

export default AffiliateLanding;
